import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import type { DraftGear } from 'lib/community/discussions-writer'
import {
  categorySheetRows,
  gearRowValue,
  pickedCategory,
  radioKeyStep,
} from 'lib/community/writer-phone'
import type { CommunityCategoryNode } from 'lib/community/types'
import BottomSheet from 'components/community/BottomSheet'
import mobile from 'components/community/Mobile.module.css'
import GearPanel from './GearPanel'
import styles from './Writer.module.css'

interface PhonePickersProps {
  /** DealHunt's category list; empty → no category row (as the desktop card). */
  categories: CommunityCategoryNode[]
  showCategory: boolean
  selected: string | null
  onSelect: (id: string | null) => void
  gear: DraftGear[]
  onAddGear: (gear: DraftGear) => void
  onRemoveGear: (key: string) => void
  gearLocked: boolean
}

type Open = 'category' | 'gear' | null

const Row: React.VFC<{
  label: string
  value: string | null
  placeholder: string
  controls: string
  expanded: boolean
  onOpen: () => void
}> = ({ label, value, placeholder, controls, expanded, onOpen }) => (
  <button
    type="button"
    className={styles.pickRow}
    aria-haspopup="dialog"
    aria-expanded={expanded}
    aria-controls={expanded ? controls : undefined}
    onClick={onOpen}
  >
    <span className={styles.pickText}>
      <span className={styles.pickLabel}>{label}</span>
      <span className={clsx(styles.pickValue, { [styles.pickEmpty]: !value })}>
        {value ?? placeholder}
      </span>
    </span>
    <span className={styles.pickChevron} aria-hidden="true">
      {'›'}
    </span>
  </button>
)

/** The Category sheet: each top-level category with its children indented ("No category" first
 *  once one is picked); a tap or Enter / Space picks and closes it. The keyboard follows the
 *  radio pattern: one tab stop (the checked row, else the first), arrows / Home / End move AND
 *  check without closing. */
export const CategorySheet: React.VFC<
  Pick<PhonePickersProps, 'categories' | 'selected' | 'onSelect'> & { onClose: () => void }
> = ({ categories, selected, onSelect, onClose }) => {
  const rows = useMemo(() => categorySheetRows(categories), [categories])
  const picked = pickedCategory(categories, selected)
  // "No category" only once something is picked: it is how a pick is undone, never a default.
  // Kept while the sheet is open, so arrowing onto it does not pull the row from under focus.
  const [undoRow, setUndoRow] = useState(Boolean(picked.rowId))
  if (picked.rowId && !undoRow) {
    setUndoRow(true)
  }
  const options = [
    ...(undoRow ? [{ id: null, name: 'No category', depth: 0 as const }] : []),
    ...rows.map(r => ({ id: r.node.id, name: r.node.name, depth: r.depth })),
  ]
  const stop = Math.max(
    0,
    options.findIndex(o => o.id === picked.rowId)
  )
  const listRef = useRef<HTMLDivElement>(null)
  // The row an arrow key checked: focused once it has rendered (rows can shift as "No category"
  // appears).
  const [focusKey, setFocusKey] = useState<string | null>(null)
  useEffect(() => {
    if (focusKey !== null) {
      listRef.current?.querySelector<HTMLElement>(`[data-cat="${focusKey}"]`)?.focus()
      setFocusKey(null)
    }
  }, [focusKey])
  const onKeyDown = (e: React.KeyboardEvent, index: number): void => {
    const next = radioKeyStep(e.key, index, options.length)
    if (next === null) {
      return
    }
    e.preventDefault()
    const row = options[next]
    if (row.id !== picked.rowId) {
      onSelect(row.id)
    }
    setFocusKey(row.id ?? 'none')
  }
  return (
    <BottomSheet
      id="writer-category-sheet"
      labelledBy="writer-category-title"
      onClose={onClose}
      withClose
    >
      <p id="writer-category-title" className={mobile.sheetTitle}>
        {'Category'}
      </p>
      <div
        ref={listRef}
        className={styles.catList}
        role="radiogroup"
        aria-labelledby="writer-category-title"
      >
        {options.map((row, index) => {
          const on = row.id === picked.rowId
          return (
            <button
              key={row.id ?? 'none'}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={index === stop ? 0 : -1}
              data-cat={row.id ?? 'none'}
              onKeyDown={e => onKeyDown(e, index)}
              className={clsx(styles.catRow, {
                [styles.catChild]: row.depth === 1,
                [styles.catNone]: row.id === null,
                [styles.catOn]: on,
              })}
              onClick={() => {
                // Compare with the row shown checked: a deeper `?category=` preset lights the
                // child above it, and re-tapping that child must not move the pick up a level.
                if (row.id !== picked.rowId) {
                  onSelect(row.id)
                }
                onClose()
              }}
            >
              <span className={styles.catName}>{row.name}</span>
              <span className={styles.catMark} aria-hidden="true">
                {on ? '✓' : ''}
              </span>
            </button>
          )
        })}
      </div>
    </BottomSheet>
  )
}

/** The Link gear sheet: full height (the field stays clear of the keyboard), the desktop
 *  GearPanel without its card. */
export const GearSheet: React.VFC<
  Pick<PhonePickersProps, 'gear' | 'onAddGear' | 'onRemoveGear' | 'gearLocked'> & {
    onClose: () => void
  }
> = ({ gear, onAddGear, onRemoveGear, gearLocked, onClose }) => (
  <BottomSheet
    id="writer-gear-sheet"
    labelledBy="writer-gear-title"
    onClose={onClose}
    withClose
    // GearPanel focuses its search field itself (as the dock's picker sheet); locked, no field.
    focusOnOpen={gearLocked}
    className={mobile.pickerSheet}
  >
    <p id="writer-gear-title" className={mobile.sheetTitle}>
      {'Link gear'}
    </p>
    <GearPanel gear={gear} onAdd={onAddGear} onRemove={onRemoveGear} locked={gearLocked} inSheet />
  </BottomSheet>
)

/**
 * The phone writer's Category and Link gear (design M1c): one 52px row each under the body —
 * label, current value, chevron — opening the community's one BottomSheet. Category is one list
 * (each top-level category, its children indented under it; "No category" first once picked), single-select,
 * a pick closes the sheet; it picks with the reducer's `setCategory`, so a later product link no
 * longer moves it — the desktop pills' rule. Link gear is the desktop GearPanel in a full-height
 * sheet (chips first, "Owners get notified", the search). Hidden above 767.98px, where the side
 * cards are the pickers; the sheets mount only after a tap.
 */
const PhonePickers: React.VFC<PhonePickersProps> = ({
  categories,
  showCategory,
  selected,
  onSelect,
  gear,
  onAddGear,
  onRemoveGear,
  gearLocked,
}) => {
  const [open, setOpen] = useState<Open>(null)
  const picked = pickedCategory(categories, selected)
  const close = (): void => setOpen(null)

  return (
    <div className={styles.picks}>
      {showCategory && (
        <Row
          label="Category"
          value={picked.label}
          placeholder="Choose one"
          controls="writer-category-sheet"
          expanded={open === 'category'}
          onOpen={() => setOpen('category')}
        />
      )}
      <Row
        label={gearLocked ? 'Link gear · Locked' : 'Link gear · Optional'}
        value={gearRowValue(gear)}
        placeholder="Owners get notified"
        controls="writer-gear-sheet"
        expanded={open === 'gear'}
        onOpen={() => setOpen('gear')}
      />

      {open === 'category' && (
        <CategorySheet
          categories={categories}
          selected={selected}
          onSelect={onSelect}
          onClose={close}
        />
      )}
      {open === 'gear' && (
        <GearSheet
          gear={gear}
          onAddGear={onAddGear}
          onRemoveGear={onRemoveGear}
          gearLocked={gearLocked}
          onClose={close}
        />
      )}
    </div>
  )
}

export default PhonePickers
